///
/// EarthBrowser
///
/// Created by Matt Giger on 9/9/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AdminOnly } from "@/components/auth/ProtectedComponent"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { setProfileOpen } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useUpdateUserProfileMutation } from "@/redux/userApi"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckIcon, Loader2, PencilIcon, Settings, UserIcon, XIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { DeleteAccountCard } from "./DeleteAccountCard"
import { SectionHeader } from "./SectionHeader"

const profileFormSchema = z.object({
	username: z.string().min(3).max(50),
	firstName: z.string().min(2).max(50),
	lastName: z.string().min(2).max(50),
	email: z.string().max(320).email(),
})

const defaultValues = {
	username: "",
	firstName: "",
	lastName: "",
	email: "",
}

export function AccountTab() {
	const dispatch = useAppDispatch()
	const userProfile = useAppSelector((state) => state.user.userProfile)
	const [updateUserProfile, { isLoading: isUpdating }] = useUpdateUserProfileMutation()
	const [isEditing, setIsEditing] = useState(false)

	const form = useForm<z.infer<typeof profileFormSchema>>({
		resolver: zodResolver(profileFormSchema),
		defaultValues,
	})

	useEffect(() => {
		if (userProfile) {
			form.reset({
				username: userProfile.username,
				firstName: userProfile.first_name,
				lastName: userProfile.last_name,
				email: userProfile.email,
			})
		}
	}, [form, userProfile])

	async function onSubmit(values: z.infer<typeof profileFormSchema>) {
		setIsEditing(false)
		await updateUserProfile({
			username: values.username,
			first_name: values.firstName,
			last_name: values.lastName,
			email: values.email,
			image_url: userProfile?.image_url ?? "",
		})
	}

	const inputClass = (editing: boolean) =>
		`rounded-lg ${editing ? 'bg-background' : 'bg-muted/50 border-transparent'}`

	return (
		<div className="space-y-6">
			{/* Admin Console Access Banner */}
			<AdminOnly>
				<div className="rounded-xl border border-border bg-muted/30 p-3 md:p-4">
					<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
						<div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
							<div className="p-2 rounded-lg bg-muted flex-shrink-0">
								<Settings className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
							</div>
							<div className="min-w-0 flex-1">
								<h3 className="text-sm sm:text-base font-semibold text-foreground">Admin Console</h3>
								<p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
									Access admin tools and system settings
								</p>
							</div>
						</div>
						<Button
							onClick={() => {
								dispatch(setProfileOpen(false))
								window.location.href = "https://admin.earthbrowser.com"
							}}
							size="sm"
							variant="secondary"
							className="w-full sm:w-auto text-xs sm:text-sm"
						>
							<Settings className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
							Open Console
						</Button>
					</div>
				</div>
			</AdminOnly>

			{/* Profile details form */}
			<div className="rounded-xl border border-border bg-muted/30 p-4">
				<SectionHeader
					icon={UserIcon}
					title="Profile Details"
					description="Your name, username and email address"
					color="blue"
					action={
						isEditing ? (
							<div className="flex gap-2">
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="rounded-full"
									onClick={() => {
										setIsEditing(false)
										form.reset()
									}}
								>
									<XIcon className="h-4 w-4 mr-1" />
									<span className="font-medium">Cancel</span>
								</Button>
								<Button
									type="submit"
									form="account-form"
									disabled={!form.formState.isDirty || !form.formState.isValid || isUpdating}
									size="sm"
									className="rounded-full"
								>
									{isUpdating ? (
										<Loader2 className="h-4 w-4 mr-1 animate-spin" />
									) : (
										<CheckIcon className="h-4 w-4 mr-1" />
									)}
									<span className="font-medium">{isUpdating ? "Saving..." : "Save"}</span>
								</Button>
							</div>
						) : (
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="rounded-full"
								onClick={() => setIsEditing(true)}
							>
								<PencilIcon className="h-4 w-4 mr-1" />
								<span className="font-medium">Edit</span>
							</Button>
						)
					}
				/>

				<Form {...form}>
					<form id="account-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField control={form.control} name="username" disabled={!isEditing} render={({ field }) => (
								<FormItem className="space-y-1.5">
									<FormLabel className="text-sm font-semibold text-foreground/80">Username</FormLabel>
									<FormControl>
										<Input placeholder="your-username" autoComplete="username" className={inputClass(isEditing)} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)} />
							<FormField control={form.control} name="email" disabled={!isEditing} render={({ field }) => (
								<FormItem className="space-y-1.5">
									<FormLabel className="text-sm font-semibold text-foreground/80">Email Address</FormLabel>
									<FormControl>
										<Input placeholder="you@example.com" autoComplete="email" className={inputClass(isEditing)} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)} />
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<FormField control={form.control} name="firstName" disabled={!isEditing} render={({ field }) => (
								<FormItem className="space-y-1.5">
									<FormLabel className="text-sm font-semibold text-foreground/80">First Name</FormLabel>
									<FormControl>
										<Input placeholder="John" autoComplete="given-name" className={inputClass(isEditing)} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)} />
							<FormField control={form.control} name="lastName" disabled={!isEditing} render={({ field }) => (
								<FormItem className="space-y-1.5">
									<FormLabel className="text-sm font-semibold text-foreground/80">Last Name</FormLabel>
									<FormControl>
										<Input placeholder="Doe" autoComplete="family-name" className={inputClass(isEditing)} {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)} />
						</div>
					</form>
				</Form>
			</div>

			{/* Permanent account deletion (store requirement) */}
			<DeleteAccountCard />
		</div>
	)
}
